import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Plane, BadgeCheck, CalendarCheck, Video, FileCheck, ArrowRight,
  Star, MapPin, Clock, Quote, Lock, ChevronRight,
} from "lucide-react";
import { api, formatAud } from "@/lib/api";
import ReadinessQuiz from "@/components/ReadinessQuiz";
import FreeResources from "@/components/FreeResources";
import Faq from "@/components/Faq";

const IMAGES = {
  hero: "https://images.unsplash.com/photo-1545132147-d037e6c54cfd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwyfHxhaXJwbGFuZSUyMHNreSUyMHdpbmclMjB2aWV3JTIwY2xvdWRzJTIwZmxpZ2h0JTIwc3Vuc2V0fGVufDB8fHx8MTc4ODkxNzA4Nnww&ixlib=rb-4.1.0&q=85",
  tony: "/tony.png",
  aisle: "https://images.unsplash.com/photo-1634274969399-48a87e7990b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxmbGlnaHQlMjBhdHRlbmRhbnQlMjBhaXJwbGFuZSUyMGNyZXclMjBjb2NrcGl0JTIwYXZpYXRpb258ZW58MHx8fGJsdWV8MTc4ODkxNzA4Nnww&ixlib=rb-4.1.0&q=85",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export const Navbar = () => (
  <header data-testid="navbar" className="fixed top-0 inset-x-0 z-50 backdrop-blur-md bg-white/85 border-b border-slate-200/70">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 h-16 flex items-center justify-between">
      <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group">
        <span className="w-9 h-9 rounded-full bg-[#0A192F] flex items-center justify-center transition-transform duration-300 group-hover:-rotate-12">
          <Plane className="w-4.5 h-4.5 w-5 h-5 text-[#F59E0B]" />
        </span>
        <span className="font-display font-bold text-xl tracking-wide text-[#0A192F] uppercase">
          Cabin Crew <span className="text-sky-600">Mentoring</span>
        </span>
      </Link>
      <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-slate-600">
        <a href="/#sessions" data-testid="nav-sessions-link" className="hover:text-[#0A192F] transition-colors duration-200">Sessions</a>
        <a href="/#about" data-testid="nav-about-link" className="hover:text-[#0A192F] transition-colors duration-200">About Tony</a>
        <a href="/#resources" data-testid="nav-resources-link" className="hover:text-[#0A192F] transition-colors duration-200">Free Resources</a>
        <Link to="/blog" data-testid="nav-blog-link" className="hover:text-[#0A192F] transition-colors duration-200">Blog</Link>
        <a href="/#faq" data-testid="nav-faq-link" className="hover:text-[#0A192F] transition-colors duration-200">FAQ</a>
        <a href="/#stories" data-testid="nav-stories-link" className="hover:text-[#0A192F] transition-colors duration-200">Success Stories</a>
        <Link to="/admin/login" data-testid="nav-admin-link" className="hover:text-[#0A192F] transition-colors duration-200 flex items-center gap-1">
          <Lock className="w-3.5 h-3.5" /> Admin
        </Link>
      </nav>
      <Link
        to="/book"
        data-testid="nav-book-btn"
        className="rounded-full bg-[#0A192F] text-white text-sm font-semibold px-5 py-2.5 hover:bg-sky-700 transition-colors duration-200 flex items-center gap-2"
      >
        Book a Session <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  </header>
);

export const Footer = () => (
  <footer data-testid="footer" className="navy-deep text-slate-400">
    <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-16 grid md:grid-cols-3 gap-12">
      <div>
        <div className="flex items-center gap-2.5 mb-4">
          <span className="w-9 h-9 rounded-full bg-white/10 flex items-center justify-center">
            <Plane className="w-5 h-5 text-[#F59E0B]" />
          </span>
          <span className="font-display font-bold text-xl tracking-wide text-white uppercase">Cabin Crew Mentoring</span>
        </div>
        <p className="text-sm leading-relaxed">Cabin crew career mentoring by Tony — former flight attendant at a major Canadian airline and two major Australian airlines, and former airline hiring team member.</p>
      </div>
      <div>
        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Sessions</h4>
        <ul className="space-y-2 text-sm">
          <li><Link to="/book?session=interview_ready" data-testid="footer-interview-link" className="hover:text-white transition-colors duration-200">Interview Ready Coaching</Link></li>
          <li><Link to="/book?session=cabin_manager" data-testid="footer-manager-link" className="hover:text-white transition-colors duration-200">Cabin Manager Mentoring</Link></li>
          <li><Link to="/book?session=training_support" data-testid="footer-training-link" className="hover:text-white transition-colors duration-200">Ground School Support</Link></li>
        </ul>
      </div>
      <div>
        <h4 className="text-white font-semibold mb-4 text-sm uppercase tracking-widest">Contact</h4>
        <ul className="space-y-2 text-sm">
          <li data-testid="footer-email">tony@cabincrewmentoring.com</li>
          <li data-testid="footer-location" className="flex items-center gap-2"><MapPin className="w-4 h-4" /> Australia — sessions via video call</li>
          <li data-testid="footer-hours" className="flex items-center gap-2"><Clock className="w-4 h-4" /> Mon – Sat, Sydney time</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-6 text-center text-xs text-slate-500">
      Cabin Crew Mentoring by Tony. All times shown in Sydney time (AEST/AEDT).
    </div>
  </footer>
);

const STAMPS = [
  { label: "Canadian Major Airline", sub: "Where it all began" },
  { label: "Australian Major Airline", sub: "Cabin Manager" },
  { label: "Australian Major Airline #2", sub: "Flight Attendant" },
  { label: "Airline Hiring Team", sub: "Interviewer & Assessor" },
];

const STORIES = [
  { name: "Jade T", badge: "Jetstar — Initial Training", text: "Tony's calm approach and experience helped me get through my initial training. Having his insight, study tips and guidance allowed me to focus on the right information and, most importantly, reduced my stress levels while increasing my confidence." },
  { name: "Tim B", badge: "Hired — Jetstar", text: "Thank you for everything, Tony. You were there when things got stressful, helped me stay focused and kept me motivated." },
  { name: "Brett M", badge: "Hired — WestJet", text: "Tony explained how my personal experiences could work in multiple ways for assessment-day interview questions. Flying was always a dream, and Tony helped make that dream come true." },
];

const STEPS = [
  { icon: CalendarCheck, title: "Pick your session & time", text: "Choose the mentoring session that fits where you are, then grab a slot that suits your schedule." },
  { icon: FileCheck, title: "Tell Tony your goal", text: "Share which airline you're applying to and where you're at, so every minute is tailored to you." },
  { icon: Video, title: "Meet over video call", text: "A focused 1:1 session with someone who sat on the other side of the interview table." },
  { icon: BadgeCheck, title: "Walk in ready", text: "Leave with a clear action plan, honest feedback, and the confidence of insider knowledge." },
];

export default function Home() {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/sessions").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      <Navbar />

      {/* HERO */}
      <section className="relative navy-panel text-white overflow-hidden">
        <img src={IMAGES.hero} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A192F]/70 via-[#0A192F]/60 to-[#0A192F]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 pt-40 pb-32">
          <motion.div initial="hidden" animate="show" variants={fadeUp} className="max-w-3xl">
            <h1 data-testid="hero-heading" className="font-display uppercase font-bold text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
              Get interview ready<br />
              <span className="text-[#F59E0B]">with someone who's</span><br />
              sat on the panel
            </h1>
            <p data-testid="hero-subheading" className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
              Tony started his career flying for a major Canadian airline, then moved to Australia to fly for two of its major airlines — and hired cabin crew himself. Now he mentors you through the interview, the training, and the step up to cabin manager.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/book"
                data-testid="hero-book-btn"
                className="rounded-full bg-[#F59E0B] text-[#0A192F] font-bold px-8 py-4 text-base hover:bg-amber-400 transition-colors duration-200 gold-glow flex items-center gap-2"
              >
                Book Your Session <ArrowRight className="w-5 h-5" />
              </Link>
              <a
                href="/#sessions"
                data-testid="hero-explore-btn"
                className="rounded-full border border-white/25 text-white font-semibold px-8 py-4 text-base hover:bg-white/10 transition-colors duration-200"
              >
                Explore Sessions
              </a>
            </div>
            <div className="mt-16 grid grid-cols-3 max-w-lg gap-6">
              {[
                ["3", "Major airlines flown"],
                ["2", "Countries, CA & AU"],
                ["100s", "Of crew interviews run"],
              ].map(([num, label]) => (
                <div key={label} data-testid={`hero-stat-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <div className="font-display text-4xl font-bold text-sky-400">{num}</div>
                  <div className="text-xs uppercase tracking-widest text-slate-400 mt-1">{label}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* ABOUT TONY */}
      <section id="about" className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="relative">
            <div className="rounded-2xl overflow-hidden sky-glow">
              <img src={IMAGES.tony} alt="Tony — cabin crew mentor" className="w-full h-auto block" />
            </div>
            <div className="absolute -bottom-6 -right-4 sm:right-8 navy-panel text-white rounded-xl px-6 py-4 shadow-2xl">
              <div className="font-display text-2xl font-bold text-[#F59E0B] uppercase">Former Hiring Team</div>
              <div className="text-xs text-slate-300 tracking-widest uppercase">He knows what they're looking for</div>
            </div>
          </motion.div>
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-700">Meet your mentor</span>
            <h2 data-testid="about-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#0A192F]">
              Tony has been exactly where you're heading
            </h2>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Tony began his career in the skies of Canada, flying for a major Canadian airline. He then crossed the Pacific to Australia, where he flew for two of the country's major airlines — from domestic hops to international long-haul. Along the way, he sat on the airline hiring team — reading applications, running assessment days, and making the call on who got their wings.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Now he's opening that playbook to you. No generic advice — just honest, insider mentoring from someone who knows exactly what the industry is looking for.
            </p>
            <div className="mt-8 grid sm:grid-cols-2 gap-3">
              {STAMPS.map((s) => (
                <div key={s.label} data-testid={`stamp-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-xl border-2 border-dashed border-sky-300 bg-sky-50 px-4 py-3 rotate-[-0.5deg] hover:rotate-0 transition-transform duration-300">
                  <div className="font-display font-bold uppercase tracking-wide text-sky-800">{s.label}</div>
                  <div className="text-xs text-sky-600 uppercase tracking-widest">{s.sub}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* READINESS QUIZ */}
      <ReadinessQuiz />

      {/* SESSIONS */}
      <section id="sessions" className="navy-panel py-24 sm:py-32 text-white relative overflow-hidden">
        <img src={IMAGES.aisle} alt="" className="absolute inset-0 w-full h-full object-cover opacity-10" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-400">Choose your flight path</span>
            <h2 data-testid="sessions-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mt-3">
              Mentoring sessions built for every stage
            </h2>
          </motion.div>
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {sessions.map((s, i) => (
              <motion.div
                key={s.key}
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.55, delay: i * 0.12 }}
                data-testid={`session-card-${s.key}`}
                className="rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm p-8 flex flex-col hover:-translate-y-1 hover:bg-white/[0.08] transition-[transform,background-color] duration-300"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs uppercase tracking-widest text-sky-400 font-semibold">{s.duration}</span>
                  <span data-testid={`session-price-${s.key}`} className="font-display text-3xl font-bold text-[#F59E0B]">{formatAud(s.price_cents)}</span>
                </div>
                <h3 className="text-xl font-semibold mt-4">{s.name}</h3>
                <p className="text-sm text-slate-300 mt-2 leading-relaxed">{s.tagline}</p>
                <ul className="mt-6 space-y-2.5 flex-1">
                  {s.features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <BadgeCheck className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/book?session=${s.key}`}
                  data-testid={`session-book-btn-${s.key}`}
                  className="mt-8 rounded-full bg-white text-[#0A192F] font-bold text-sm px-6 py-3 text-center hover:bg-[#F59E0B] transition-colors duration-200 flex items-center justify-center gap-2"
                >
                  Book This Session <ChevronRight className="w-4 h-4" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-700">Flight plan</span>
            <h2 data-testid="how-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#0A192F]">How booking works</h2>
          </motion.div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {STEPS.map((step, i) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`step-card-${i}`}
                className="rounded-xl bg-white border border-slate-200 p-6 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
              >
                <div className="w-11 h-11 rounded-full bg-sky-100 flex items-center justify-center">
                  <step.icon className="w-5 h-5 text-sky-700" />
                </div>
                <div className="font-display text-sm font-bold text-sky-600 mt-4 uppercase tracking-widest">Step {i + 1}</div>
                <h3 className="font-semibold mt-1 text-[#0A192F]">{step.title}</h3>
                <p className="text-sm text-slate-500 mt-2 leading-relaxed">{step.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FREE RESOURCES */}
      <FreeResources />

      {/* SUCCESS STORIES */}
      <section id="stories" className="bg-sky-50 py-24 sm:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] font-semibold text-sky-700">Cleared for takeoff</span>
            <h2 data-testid="stories-heading" className="text-3xl sm:text-4xl font-bold tracking-tight mt-3 text-[#0A192F]">What Tony's mentees say</h2>
          </motion.div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {STORIES.map((story, i) => (
              <motion.figure
                key={story.name}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                data-testid={`story-card-${i}`}
                className="rounded-xl bg-white border border-slate-200 p-8 flex flex-col w-full hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
              >
                <Quote className="w-8 h-8 text-sky-200" />
                <blockquote className="mt-4 text-slate-600 leading-relaxed flex-1 text-sm">{story.text}</blockquote>
                <figcaption className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-[#0A192F]">{story.name}</div>
                    <div className="flex gap-0.5 mt-1">
                      {[...Array(5)].map((_, s) => <Star key={s} className="w-3.5 h-3.5 fill-[#F59E0B] text-[#F59E0B]" />)}
                    </div>
                  </div>
                  <span data-testid={`story-airline-${i}`} className="rounded-full bg-[#0A192F] text-white text-xs font-bold px-3.5 py-1.5 uppercase tracking-wider">
                    {story.badge}
                  </span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <Faq />

      {/* CTA */}
      <section className="navy-deep py-24 text-white text-center relative overflow-hidden">
        <div className="relative max-w-3xl mx-auto px-4">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}>
            <h2 data-testid="cta-heading" className="font-display uppercase font-bold text-4xl sm:text-5xl tracking-tight">
              Your assessment day is boarding.<br /><span className="text-[#F59E0B]">Are you on it?</span>
            </h2>
            <Link
              to="/book"
              data-testid="cta-book-btn"
              className="inline-flex items-center gap-2 mt-10 rounded-full bg-[#F59E0B] text-[#0A192F] font-bold px-10 py-4 hover:bg-amber-400 transition-colors duration-200 gold-glow"
            >
              Book Your Session Now <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
